import React, { useEffect, useId, useState } from 'react';
import Dialog from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import Notice from '../common/Notice';
import './SaenggibuAnalysisReviewModal.css';

const CONFIDENCE_LEVELS = ['high', 'medium', 'low'];

const buildFieldValues = (editFields) => editFields.reduce((acc, field) => {
  acc[field.key] = field.value || '';
  return acc;
}, {});

/**
 * 파일 분석 결과 확인 모달 (Dialog 프리미티브 위)
 * - AI가 파일에서 읽은 내용을 교사가 확인·수정한 뒤 입력칸에 넣는다.
 * - 신뢰도·주의사항은 색을 칠한 상자 대신 Notice(info/warning)로 보여준다.
 * - props API 유지: { isOpen, review, onConfirm, onClose, onReset }
 */
const SaenggibuAnalysisReviewModal = ({
  isOpen,
  review,
  onConfirm,
  onClose,
  onReset
}) => {
  const textareaId = useId();
  const [isEditing, setIsEditing] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [textEdited, setTextEdited] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const editFields = Array.isArray(review?.editFields) ? review.editFields : [];
    setReviewText(review?.text || '');
    setFieldValues(buildFieldValues(editFields));
    setIsEditing(Boolean(review?.startEditing));
    setTextEdited(false);
  }, [isOpen, review]);

  if (!isOpen || !review) return null;

  const warnings = Array.isArray(review.warnings) ? review.warnings.filter(Boolean) : [];
  const summaryItems = Array.isArray(review.summaryItems) ? review.summaryItems.filter(Boolean) : [];
  const confidence = review.confidence || review.analyzedData?.confidence;
  const hasKnownConfidence = CONFIDENCE_LEVELS.includes(confidence);
  const isLowConfidence = confidence === 'low';
  const isMediumConfidence = confidence === 'medium';
  const rawText = review.rawText || review.text || '';
  const editFields = Array.isArray(review.editFields) ? review.editFields : [];
  const hasEditFields = editFields.length > 0;

  // 주의가 필요한 경우만 안내 띠를 띄운다. 색은 아이콘·왼쪽 막대에만 쓴다.
  const showAttentionNotice = warnings.length > 0 || isLowConfidence || isMediumConfidence;
  const noticeVariant = warnings.length > 0 || isLowConfidence ? 'warning' : 'info';
  let noticeTitle = '한 번 더 확인해주세요';
  if (warnings.length > 0) noticeTitle = '확인해주세요';
  else if (isLowConfidence) noticeTitle = 'AI가 확신하지 못한 부분이 있어요';

  const handleConfirm = () => {
    onConfirm?.({
      ...review,
      text: hasEditFields ? (fieldValues.text ?? reviewText) : reviewText,
      fieldValues,
      textEdited
    });
  };

  const handleBackToSummary = () => {
    setIsEditing(false);
    setReviewText(review.text || '');
    setFieldValues(buildFieldValues(editFields));
    setTextEdited(false);
  };

  const handleReset = () => {
    if (onReset) onReset(review);
    else onClose?.();
  };

  const primaryLabel = textEdited
    ? '수정한 내용으로 넣기'
    : (review.confirmLabel || '이 내용으로 넣기');

  const footer = (
    <ModalActions
      primary={{ label: primaryLabel, onClick: handleConfirm }}
      secondary={isEditing
        ? { label: '요약으로 돌아가기', onClick: handleBackToSummary }
        : { label: '수정하기', onClick: () => setIsEditing(true) }}
      tertiary={isEditing
        ? null
        : { label: review.resetLabel || '다시 업로드', onClick: handleReset }}
      testId="sgb-analysis-review-actions"
    />
  );

  return (
    <Dialog
      description={review.description || '잘못 읽은 부분을 고치면 더 정확한 문구가 나와요.'}
      eyebrow="생성 전 확인"
      footer={footer}
      onClose={onClose}
      open
      panelClassName="sgb-analysis-review"
      size="lg"
      testId="sgb-analysis-review"
      title={review.title || 'AI가 파일 내용을 이렇게 읽었어요'}
    >
      {(review.categoryLabel || review.itemLabel || confidence) && (
        <div className="sgb-analysis-review-meta">
          {review.categoryLabel && (
            <span className="sgb-analysis-review-chip">{review.categoryLabel}</span>
          )}
          {review.itemLabel && (
            <span className="sgb-analysis-review-chip">{review.itemLabel}</span>
          )}
          {confidence === 'high' && (
            <span className="sgb-analysis-review-chip">신뢰도 높음</span>
          )}
          {confidence && !hasKnownConfidence && (
            <span className="sgb-analysis-review-chip">신뢰도 {confidence}</span>
          )}
        </div>
      )}

      {showAttentionNotice && (
        <Notice
          className="sgb-analysis-review-notice"
          testId="sgb-analysis-review-notice"
          title={noticeTitle}
          variant={noticeVariant}
        >
          {warnings.length > 0 ? (
            <ul className="sgb-analysis-review-warning-list">
              {warnings.map((warning, index) => (
                <li key={`${warning}-${index}`}>{warning}</li>
              ))}
            </ul>
          ) : (
            <p>
              {isLowConfidence
                ? '잘못 읽은 부분이 있으면 고쳐서 넣어주세요.'
                : '일부 내용이 다르게 읽혔을 수 있어요.'}
            </p>
          )}
        </Notice>
      )}

      {summaryItems.length > 0 && !isEditing && (
        <div className="sgb-analysis-review-summary">
          {summaryItems.map((item, index) => (
            <div className="sgb-analysis-review-summary-item" key={`${item.label}-${index}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      )}

      {isEditing ? (
        <div className="sgb-analysis-review-edit">
          {hasEditFields ? (
            editFields.map((field, index) => {
              const fieldId = `${textareaId}-${field.key || index}`;
              return (
                <div className="sgb-analysis-review-edit-field" key={field.key || index}>
                  <label className="sgb-analysis-review-edit-label" htmlFor={fieldId}>
                    {field.label}
                  </label>
                  <textarea
                    id={fieldId}
                    className="sgb-analysis-review-textarea"
                    value={fieldValues[field.key] || ''}
                    onChange={(event) => {
                      setFieldValues(prev => ({
                        ...prev,
                        [field.key]: event.target.value
                      }));
                      setTextEdited(true);
                    }}
                    rows={field.rows || 6}
                  />
                </div>
              );
            })
          ) : (
            <>
              <label className="sgb-analysis-review-edit-label" htmlFor={textareaId}>
                {review.editLabel || '분석 내용 수정'}
              </label>
              <textarea
                id={textareaId}
                className="sgb-analysis-review-textarea"
                value={reviewText}
                onChange={(event) => {
                  setReviewText(event.target.value);
                  setTextEdited(true);
                }}
                rows={10}
              />
            </>
          )}
          <p className="sgb-analysis-review-edit-hint">
            수정한 내용은 입력칸에 그대로 들어가요.
          </p>
        </div>
      ) : (
        <details className="sgb-analysis-review-raw" open>
          <summary>{review.rawSummaryLabel || '입력될 내용 보기'}</summary>
          <pre>{rawText}</pre>
        </details>
      )}
    </Dialog>
  );
};

export default SaenggibuAnalysisReviewModal;
